import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { toast } from 'react-hot-toast';

const FinancialDetails = ({
  asset,
  isEditing,
  onUpdate,
  userRole = 'standard'
}) => {
  const [showScheduleTable, setShowScheduleTable] = useState(true);

  // Currency configuration
  const currencyOptions = [
    { value: 'inr', label: 'INR (₹) - Indian Rupee' },
    { value: 'usd', label: 'USD ($) - US Dollar' },
    { value: 'eur', label: 'EUR (€) - Euro' },
    { value: 'gbp', label: 'GBP (£) - British Pound' },
    { value: 'cad', label: 'CAD ($) - Canadian Dollar' },
    { value: 'aud', label: 'AUD ($) - Australian Dollar' },
    { value: 'jpy', label: 'JPY (¥) - Japanese Yen' },
    { value: 'aed', label: 'AED (AED) - UAE Dirham' }
  ];

  const currencyConfig = {
    inr: { symbol: '₹', locale: 'en-IN', currency: 'INR' },
    usd: { symbol: '$', locale: 'en-US', currency: 'USD' },
    eur: { symbol: '€', locale: 'de-DE', currency: 'EUR' },
    gbp: { symbol: '£', locale: 'en-GB', currency: 'GBP' },
    cad: { symbol: 'CA$', locale: 'en-CA', currency: 'CAD' },
    aud: { symbol: 'A$', locale: 'en-AU', currency: 'AUD' },
    jpy: { symbol: '¥', locale: 'ja-JP', currency: 'JPY' },
    aed: { symbol: 'AED', locale: 'ar-AE', currency: 'AED' }
  };

  const depreciationMethodOptions = [
    { value: 'straight-line', label: 'Straight Line (Equal Annual Write-off)' },
    { value: 'declining-balance', label: 'Declining Balance (Accelerated IT Write-off)' },
    { value: 'sum-of-years', label: 'Sum of Years Digits (Front-loaded)' },
    { value: 'none', label: 'No Depreciation (Fixed / Land / Intangible)' }
  ];

  const paymentMethodOptions = [
    { value: 'Bank Transfer / NEFT', label: 'Bank Transfer / NEFT' },
    { value: 'Corporate Credit Card', label: 'Corporate Credit Card' },
    { value: 'Purchase Order / Net 30', label: 'Purchase Order / Net 30' },
    { value: 'Equipment Lease', label: 'Equipment Lease' },
    { value: 'Direct Debit', label: 'Direct Debit' },
    { value: 'Cheque', label: 'Cheque' },
    { value: 'Cash', label: 'Cash' }
  ];

  const selectedCurrencyKey = (asset?.currency || 'inr').toLowerCase().trim();
  const currentCurrency = currencyConfig[selectedCurrencyKey] || currencyConfig.inr;

  const formatCurrency = (value) => {
    const amount = Number(value) || 0;
    try {
      return new Intl.NumberFormat(currentCurrency.locale, {
        style: 'currency',
        currency: currentCurrency.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    } catch (e) {
      return `${currentCurrency.symbol}${amount.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    }
  };

  // Field change handler
  const handleFieldChange = (field, value) => {
    const updated = {
      ...asset,
      [field]: value
    };

    // If purchase_price changes, synchronize with asset.price
    if (field === 'purchase_price') {
      updated.price = value;
    }

    onUpdate(updated);
  };

  // Financial calculations
  const purchasePrice = parseFloat(asset?.purchase_price ?? asset?.price ?? 0) || 0;
  const salvageValue = parseFloat(asset?.salvage_value ?? 0) || 0;
  const usefulLife = Math.max(1, parseFloat(asset?.useful_life_years ?? 5) || 5);
  const depreciationMethod = asset?.depreciation_method || 'straight-line';

  // Determine purchase date & age
  const purchaseDateObj = useMemo(() => {
    if (asset?.purchase_date) {
      const d = new Date(asset.purchase_date);
      if (!isNaN(d.getTime())) return d;
    }
    if (asset?.created_at) {
      const d = new Date(asset.created_at);
      if (!isNaN(d.getTime())) return d;
    }
    return new Date();
  }, [asset?.purchase_date, asset?.created_at]);

  const purchaseYear = purchaseDateObj.getFullYear();
  const currentYear = new Date().getFullYear();

  // Precise asset age in years
  const ageInMs = Math.max(0, Date.now() - purchaseDateObj.getTime());
  const rawAgeYears = ageInMs / (1000 * 60 * 60 * 24 * 365.25);
  const currentAgeYears = Math.min(usefulLife, Math.max(0, rawAgeYears));

  // Generate Year-by-Year Schedule
  const schedule = useMemo(() => {
    const rows = [];
    const depreciableBase = Math.max(0, purchasePrice - salvageValue);

    if (depreciationMethod === 'none' || usefulLife <= 0 || purchasePrice <= 0) {
      for (let i = 1; i <= Math.max(1, usefulLife); i++) {
        rows.push({
          yearNumber: i,
          calendarYear: purchaseYear + i - 1,
          beginningBookValue: purchasePrice,
          depreciationExpense: 0,
          accumulatedDepreciation: 0,
          endingBookValue: purchasePrice,
          isCompleted: currentYear > purchaseYear + i - 1,
          isCurrent: currentYear === purchaseYear + i - 1
        });
      }
      return rows;
    }

    if (depreciationMethod === 'declining-balance') {
      // 200% declining balance
      const rate = Math.min(1, 2 / usefulLife);
      let currentBook = purchasePrice;
      let totalAcc = 0;

      for (let i = 1; i <= usefulLife; i++) {
        const startBook = currentBook;
        let expense = startBook * rate;
        if (currentBook - expense < salvageValue) {
          expense = Math.max(0, currentBook - salvageValue);
        }
        currentBook = Math.max(salvageValue, startBook - expense);
        totalAcc += expense;

        const calYear = purchaseYear + i - 1;
        rows.push({
          yearNumber: i,
          calendarYear: calYear,
          beginningBookValue: startBook,
          depreciationExpense: expense,
          accumulatedDepreciation: totalAcc,
          endingBookValue: currentBook,
          isCompleted: currentYear > calYear,
          isCurrent: currentYear === calYear
        });
      }
      return rows;
    }

    if (depreciationMethod === 'sum-of-years') {
      const sumYears = (usefulLife * (usefulLife + 1)) / 2;
      let currentBook = purchasePrice;
      let totalAcc = 0;

      for (let i = 1; i <= usefulLife; i++) {
        const startBook = currentBook;
        const fraction = (usefulLife - i + 1) / sumYears;
        const expense = Math.min(startBook - salvageValue, depreciableBase * fraction);
        currentBook = Math.max(salvageValue, startBook - expense);
        totalAcc += expense;

        const calYear = purchaseYear + i - 1;
        rows.push({
          yearNumber: i,
          calendarYear: calYear,
          beginningBookValue: startBook,
          depreciationExpense: expense,
          accumulatedDepreciation: totalAcc,
          endingBookValue: currentBook,
          isCompleted: currentYear > calYear,
          isCurrent: currentYear === calYear
        });
      }
      return rows;
    }

    // Default: Straight-Line
    const annualExpense = usefulLife > 0 ? depreciableBase / usefulLife : 0;
    let currentBook = purchasePrice;
    let totalAcc = 0;

    for (let i = 1; i <= usefulLife; i++) {
      const startBook = currentBook;
      const expense = Math.min(startBook - salvageValue, annualExpense);
      currentBook = Math.max(salvageValue, startBook - expense);
      totalAcc += expense;

      const calYear = purchaseYear + i - 1;
      rows.push({
        yearNumber: i,
        calendarYear: calYear,
        beginningBookValue: startBook,
        depreciationExpense: expense,
        accumulatedDepreciation: totalAcc,
        endingBookValue: currentBook,
        isCompleted: currentYear > calYear,
        isCurrent: currentYear === calYear
      });
    }
    return rows;
  }, [purchasePrice, salvageValue, usefulLife, depreciationMethod, purchaseYear, currentYear]);

  // Interpolated metrics for today
  const completedYears = Math.min(usefulLife, Math.floor(currentAgeYears));
  const fractionOfYear = currentAgeYears - completedYears;

  let accumulatedDepreciation = 0;
  let annualDepreciation = 0;

  if (schedule.length > 0) {
    const currentScheduleRow = schedule[Math.min(completedYears, schedule.length - 1)];
    annualDepreciation = currentScheduleRow?.depreciationExpense || 0;

    if (completedYears >= usefulLife) {
      accumulatedDepreciation = schedule[schedule.length - 1]?.accumulatedDepreciation || 0;
    } else {
      const priorAccum = completedYears > 0 ? schedule[completedYears - 1].accumulatedDepreciation : 0;
      accumulatedDepreciation = priorAccum + (annualDepreciation * fractionOfYear);
    }
  }

  const currentBookValue = Math.max(salvageValue, purchasePrice - accumulatedDepreciation);
  const percentDepreciated = purchasePrice > 0 ? Math.min(100, Math.max(0, (accumulatedDepreciation / purchasePrice) * 100)) : 0;
  const percentRemaining = Math.max(0, 100 - percentDepreciated);

  // Total Maintenance Cost & TCO
  const maintenanceRecords = asset?.maintenance_records || asset?.maintenanceRecords || [];
  const totalMaintenanceCost = maintenanceRecords.reduce((sum, r) => sum + (parseFloat(r.cost) || 0), 0);
  const totalCostOfOwnership = purchasePrice + totalMaintenanceCost;

  // Export schedule to CSV
  const handleExportCSV = () => {
    if (!schedule || schedule.length === 0) {
      toast.error('No schedule data available to export');
      return;
    }

    const headers = 'Year,Calendar Year,Opening Book Value,Depreciation Expense,Accumulated Depreciation,Ending Book Value\n';
    const rows = schedule.map(r => 
      `Year ${r.yearNumber},${r.calendarYear},${r.beginningBookValue.toFixed(2)},${r.depreciationExpense.toFixed(2)},${r.accumulatedDepreciation.toFixed(2)},${r.endingBookValue.toFixed(2)}`
    ).join('\n');

    const blob = new Blob([headers + rows], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${asset?.asset_id || 'asset'}_depreciation_schedule.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Depreciation schedule downloaded');
  };

  return (
    <div className="space-y-6">
      
      {/* =========================================================================
          HERO FINANCIAL KPI METRIC CARDS
         ========================================================================= */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
        
        {/* Original Cost */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-primary/40 transition-colors">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Purchase Price</span>
            <Icon name="DollarSign" size={16} className="text-primary" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-foreground truncate">
            {formatCurrency(purchasePrice)}
          </p>
          <span className="text-[11px] text-muted-foreground">Original acquisition</span>
        </div>

        {/* Current Book Value */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-success/40 transition-colors">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Current Book Value</span>
            <Icon name="TrendingDown" size={16} className="text-success" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-success truncate">
            {formatCurrency(currentBookValue)}
          </p>
          <span className="text-[11px] text-success font-medium">
            {percentRemaining.toFixed(1)}% remaining
          </span>
        </div>

        {/* Accumulated Depreciation */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-warning/40 transition-colors">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Accumulated Dep.</span>
            <Icon name="Clock" size={16} className="text-warning" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-warning truncate">
            {formatCurrency(accumulatedDepreciation)}
          </p>
          <span className="text-[11px] text-warning font-medium">
            {percentDepreciated.toFixed(1)}% written off
          </span>
        </div>

        {/* Annual Depreciation Rate */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-primary/40 transition-colors">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Annual Dep.</span>
            <Icon name="Calendar" size={16} className="text-primary" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-foreground truncate">
            {formatCurrency(annualDepreciation)}
          </p>
          <span className="text-[11px] text-muted-foreground">Per fiscal year</span>
        </div>

        {/* Salvage Scrap Value */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-accent/40 transition-colors">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Salvage Value</span>
            <Icon name="Shield" size={16} className="text-accent" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-foreground truncate">
            {formatCurrency(salvageValue)}
          </p>
          <span className="text-[11px] text-muted-foreground">Floor value</span>
        </div>

        {/* Total Cost of Ownership (TCO) */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-subtle hover:border-primary/40 transition-colors">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider">Total Cost (TCO)</span>
            <Icon name="Layers" size={16} className="text-primary" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-foreground truncate">
            {formatCurrency(totalCostOfOwnership)}
          </p>
          <span className="text-[11px] text-muted-foreground">
            Price + Repairs ({formatCurrency(totalMaintenanceCost)})
          </span>
        </div>

      </div>

      {/* =========================================================================
          DEPRECIATION LIFECYCLE PROGRESS BAR
         ========================================================================= */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-subtle space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <Icon name="Activity" size={18} className="text-primary" />
            <h4 className="text-sm font-bold text-foreground">Asset Valuation & Depreciation Lifecycle</h4>
          </div>
          <div className="flex items-center space-x-3 text-xs text-muted-foreground">
            <span>
              Age: <strong className="text-foreground">{currentAgeYears.toFixed(1)} yrs</strong> of {usefulLife} yrs useful life
            </span>
            <span>•</span>
            <span className="capitalize">
              Method: <strong className="text-foreground">{depreciationMethod.replace('-', ' ')}</strong>
            </span>
          </div>
        </div>

        {/* Colored Segmented Progress Bar */}
        <div className="w-full h-3.5 bg-muted rounded-full overflow-hidden flex shadow-inner">
          <div
            style={{ width: `${percentDepreciated}%` }}
            className="bg-warning h-full transition-all duration-500 rounded-l-full relative group"
            title={`Depreciated: ${percentDepreciated.toFixed(1)}%`}
          ></div>
          <div
            style={{ width: `${percentRemaining}%` }}
            className="bg-success h-full transition-all duration-500 rounded-r-full relative group"
            title={`Remaining Book Value: ${percentRemaining.toFixed(1)}%`}
          ></div>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <div className="flex items-center space-x-2">
            <span className="w-2.5 h-2.5 rounded-full bg-warning inline-block"></span>
            <span>Depreciated: {formatCurrency(accumulatedDepreciation)} ({percentDepreciated.toFixed(1)}%)</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="w-2.5 h-2.5 rounded-full bg-success inline-block"></span>
            <span>Current Net Value: {formatCurrency(currentBookValue)} ({percentRemaining.toFixed(1)}%)</span>
          </div>
          <div className="flex items-center space-x-2 hidden sm:flex">
            <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground inline-block"></span>
            <span>Salvage Floor: {formatCurrency(salvageValue)}</span>
          </div>
        </div>
      </div>

      {/* =========================================================================
          MAIN EDITABLE INPUT FORM
         ========================================================================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* LEFT CARD: PURCHASE & VENDOR DETAILS */}
        <div className="bg-card border border-border rounded-xl p-5 shadow-subtle space-y-4">
          <div className="flex items-center space-x-2 pb-2 border-b border-border">
            <Icon name="ShoppingCart" size={18} className="text-primary" />
            <h4 className="text-sm font-bold text-foreground">Purchase & Vendor Information</h4>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Purchase Price
              </label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={asset?.purchase_price ?? asset?.price ?? ''}
                onChange={(e) => handleFieldChange('purchase_price', e.target.value)}
                disabled={!isEditing}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Currency
              </label>
              <Select
                options={currencyOptions}
                value={selectedCurrencyKey}
                onChange={(val) => handleFieldChange('currency', val)}
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Purchase Order (PO) Number
              </label>
              <Input
                type="text"
                placeholder="PO-2024-XXXX"
                value={asset?.purchase_order_number || ''}
                onChange={(e) => handleFieldChange('purchase_order_number', e.target.value)}
                disabled={!isEditing}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Invoice Number
              </label>
              <Input
                type="text"
                placeholder="INV-XXXXX"
                value={asset?.invoice_number || ''}
                onChange={(e) => handleFieldChange('invoice_number', e.target.value)}
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Vendor / Supplier
              </label>
              <Input
                type="text"
                placeholder="e.g. Dell India, Apple, CDW"
                value={asset?.vendor || ''}
                onChange={(e) => handleFieldChange('vendor', e.target.value)}
                disabled={!isEditing}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Payment Method
              </label>
              <Select
                options={paymentMethodOptions}
                value={asset?.payment_method || 'Bank Transfer / NEFT'}
                onChange={(val) => handleFieldChange('payment_method', val)}
                disabled={!isEditing}
              />
            </div>
          </div>
        </div>

        {/* RIGHT CARD: DEPRECIATION & VALUATION PARAMETERS */}
        <div className="bg-card border border-border rounded-xl p-5 shadow-subtle space-y-4">
          <div className="flex items-center space-x-2 pb-2 border-b border-border">
            <Icon name="Calculator" size={18} className="text-primary" />
            <h4 className="text-sm font-bold text-foreground">Depreciation & Accounting Valuation</h4>
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              Depreciation Method
            </label>
            <Select
              options={depreciationMethodOptions}
              value={asset?.depreciation_method || 'straight-line'}
              onChange={(val) => handleFieldChange('depreciation_method', val)}
              disabled={!isEditing}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Useful Life (Years)
              </label>
              <Input
                type="number"
                min="1"
                max="50"
                placeholder="5"
                value={asset?.useful_life_years ?? '5'}
                onChange={(e) => handleFieldChange('useful_life_years', e.target.value)}
                disabled={!isEditing}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Salvage Value ({currentCurrency.symbol})
              </label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={asset?.salvage_value ?? ''}
                onChange={(e) => handleFieldChange('salvage_value', e.target.value)}
                disabled={!isEditing}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Insured Value ({currentCurrency.symbol})
              </label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={asset?.insurance_value ?? ''}
                onChange={(e) => handleFieldChange('insurance_value', e.target.value)}
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="p-3 bg-muted/30 rounded-xl border border-border text-xs space-y-1.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Depreciation Basis:</span>
              <strong className="text-foreground">{formatCurrency(Math.max(0, purchasePrice - salvageValue))}</strong>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Monthly Depreciation:</span>
              <strong className="text-foreground">{formatCurrency(annualDepreciation / 12)}</strong>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Remaining Useful Period:</span>
              <strong className="text-foreground">{Math.max(0, usefulLife - currentAgeYears).toFixed(1)} Years</strong>
            </div>
          </div>
        </div>

      </div>

      {/* =========================================================================
          BUDGET & GL ALLOCATION
         ========================================================================= */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-subtle space-y-4">
        <div className="flex items-center space-x-2 pb-2 border-b border-border">
          <Icon name="PieChart" size={18} className="text-primary" />
          <h4 className="text-sm font-bold text-foreground">Budgeting, Cost Center & GL Accounts</h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              Cost Center
            </label>
            <Input
              type="text"
              placeholder="e.g. CC-IT-101, CC-CORP"
              value={asset?.cost_center || ''}
              onChange={(e) => handleFieldChange('cost_center', e.target.value)}
              disabled={!isEditing}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              Budget Code
            </label>
            <Input
              type="text"
              placeholder="e.g. CAPEX-2024-Q1, OPEX-HARDWARE"
              value={asset?.budget_code || ''}
              onChange={(e) => handleFieldChange('budget_code', e.target.value)}
              disabled={!isEditing}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">
              General Ledger (GL) Account
            </label>
            <Input
              type="text"
              placeholder="e.g. 1500-IT-HARDWARE"
              value={asset?.gl_account || ''}
              onChange={(e) => handleFieldChange('gl_account', e.target.value)}
              disabled={!isEditing}
            />
          </div>
        </div>
      </div>

      {/* =========================================================================
          YEAR-BY-YEAR DEPRECIATION SCHEDULE TABLE
         ========================================================================= */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-subtle space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Table" size={18} className="text-primary" />
            <h4 className="text-sm font-bold text-foreground">Year-by-Year Depreciation Schedule</h4>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="xs"
              onClick={handleExportCSV}
            >
              <Icon name="Download" size={13} className="mr-1" />
              Export CSV
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={() => setShowScheduleTable(!showScheduleTable)}
            >
              <Icon name={showScheduleTable ? "ChevronUp" : "ChevronDown"} size={14} className="mr-1" />
              {showScheduleTable ? "Collapse Table" : "Expand Table"}
            </Button>
          </div>
        </div>

        {showScheduleTable && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-border text-muted-foreground bg-muted/20">
                  <th className="py-2.5 px-3 font-semibold">Fiscal Year</th>
                  <th className="py-2.5 px-3 font-semibold">Beginning Book Value</th>
                  <th className="py-2.5 px-3 font-semibold">Depreciation Expense</th>
                  <th className="py-2.5 px-3 font-semibold">Accumulated Depreciation</th>
                  <th className="py-2.5 px-3 font-semibold">Ending Book Value</th>
                  <th className="py-2.5 px-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {schedule.map((row) => (
                  <tr
                    key={row.yearNumber}
                    className={`transition-colors ${
                      row.isCurrent ? 'bg-primary/5 font-medium' : 'hover:bg-muted/30'
                    }`}
                  >
                    <td className="py-3 px-3">
                      <div className="flex items-center space-x-2">
                        <span className="w-5 h-5 rounded-full bg-muted text-[11px] font-bold flex items-center justify-center">
                          {row.yearNumber}
                        </span>
                        <span className="font-semibold text-foreground">
                          Year {row.yearNumber} ({row.calendarYear})
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-3 font-mono text-foreground">
                      {formatCurrency(row.beginningBookValue)}
                    </td>
                    <td className="py-3 px-3 font-mono text-warning">
                      -{formatCurrency(row.depreciationExpense)}
                    </td>
                    <td className="py-3 px-3 font-mono text-muted-foreground">
                      {formatCurrency(row.accumulatedDepreciation)}
                    </td>
                    <td className="py-3 px-3 font-mono font-bold text-foreground">
                      {formatCurrency(row.endingBookValue)}
                    </td>
                    <td className="py-3 px-3">
                      {row.isCurrent ? (
                        <span className="px-2 py-0.5 rounded-full bg-primary/20 text-primary text-[10px] font-semibold border border-primary/30 inline-flex items-center">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse mr-1"></span>
                          Current Year
                        </span>
                      ) : row.isCompleted ? (
                        <span className="px-2 py-0.5 rounded-full bg-success/15 text-success text-[10px] font-semibold">
                          Completed
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[10px] font-medium">
                          Projected
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* =========================================================================
          FINANCIAL NOTES & AUDIT COMMENTS
         ========================================================================= */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-subtle space-y-3">
        <div className="flex items-center space-x-2 pb-2 border-b border-border">
          <Icon name="FileText" size={18} className="text-primary" />
          <h4 className="text-sm font-bold text-foreground">Accounting Notes & Justification</h4>
        </div>

        <textarea
          rows={3}
          value={asset?.financial_notes || ''}
          disabled={!isEditing}
          onChange={(e) => handleFieldChange('financial_notes', e.target.value)}
          className="w-full px-3 py-2 text-xs rounded-xl bg-background text-foreground border border-border focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none placeholder:text-muted-foreground"
          placeholder="Record any capital expenditure approvals, tax treatment notes, lease terms, or asset write-down justifications..."
        />
      </div>

    </div>
  );
};

export default FinancialDetails;
